"use client";

import { LinkHandler } from "@/components/common/LinkHandler";
import Tag, { TagData } from "@/components/common/Tag/Tag";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

export type ProductsSectionProduct = {
  name: string;
  description?: string;
  shortDescription?: string;
  image?: string;
  desktopImage?: string;
  images?: string[];
  button: { url: string; label: string };
  tag?: TagData;
  className?: string;
};

const ProductsSectionCard = ({ product }: { product: ProductsSectionProduct }) => {
  const {
    name,
    description,
    shortDescription,
    image,
    desktopImage,
    images,
    button,
    tag,
    className,
  } = product;

  const onClick = () => {
    GA4.onMarketingCtaClicked({
      destination: button.url,
      type: "button",
      text: button.label,
      module_name: "product",
      module_location: "mid_fold",
    });
  };

  return (
    <div
      className={cn(
        "w-full h-full bg-nova-base-white border-t border-nova-transparency-black-20",
        "even:border-l [&:nth-child(n+3)]:border-b",
        "md:border-b md:border-l md:[&:nth-child(3)]:border-r",
        className
      )}
    >
      <LinkHandler
        path={button.url}
        onClick={onClick}
        className="px-4 md:px-6 py-6 md:pb-8 flex flex-col h-full"
      >
        <div>
          <div className="hidden lg:flex justify-between items-center">
            <TextElementNova
              text={name}
              element="h3"
              className="Body-30-Dia-Strong text-left mb-1"
            />
            {tag && <Tag data={tag} />}
          </div>
          {description ? (
            <TextElementNova
              text={description}
              element="p"
              className="Body-10-Dia-Normal text-center lg:text-left hidden lg:block mb-4"
            />
          ) : null}
          <div className="flex flex-col items-center gap-2 justify-center lg:hidden">
            {tag && <Tag data={tag} />}
            {shortDescription ? (
              <TextElementNova
                text={shortDescription}
                element="p"
                className="Body-20-Dia-Normal text-center mb-2 md:mb-4"
              />
            ) : null}
          </div>
        </div>
        <div className="mt-auto">
          {images?.length ? (
            <div className="flex items-end justify-center overflow-hidden px-4 md:px-3 lg:px-4 mb-2 md:mb-4 lg:mb-8">
              {/*
               * Bare img rather than Picture: these bottles overlap via a
               * percentage margin, which resolves against the containing block,
               * so each image has to be the flex item itself. A <picture>
               * wrapper would become the flex item and change the offset.
               */}
              {images.map((src, i) => (
                /* eslint-disable-next-line no-restricted-syntax */
                <img
                  key={src}
                  src={src}
                  alt={`Bottle of Nutrafol ${name}`}
                  className={cn("max-w-none", i > 0 && "-ml-[55%]")}
                />
              ))}
            </div>
          ) : (
            image && (
              <Picture
                src={image}
                sources={
                  desktopImage ? [{ srcSet: desktopImage, media: "(min-width: 1024px)" }] : []
                }
                alt={`Bottle of Nutrafol ${name}`}
                className="px-4 md:px-3 lg:px-4 mb-2 md:mb-4 lg:mb-8"
              />
            )
          )}
          <div className="md:-mx-[15px] lg:mx-0">
            <ButtonNova label={button.label} level="Textlink" className="mx-auto lg:!hidden" />
            <ButtonNova
              label="Shop Now"
              level="Primary"
              icon="Right"
              className="mx-auto !hidden lg:!flex"
            />
          </div>
        </div>
      </LinkHandler>
    </div>
  );
};

export default ProductsSectionCard;
